import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function CardSuccessModal({
  cardId,
  cardAddress,
  share2,
  onClose,
}) {
  const navigate = useNavigate();
  const [writingNfc, setWritingNfc] = useState(false);
  const [showManualWrite, setShowManualWrite] = useState(false);
  const [nfcSupported, setNfcSupported] = useState("NDEFReader" in window);

  // Detect if user is on iOS
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

  const handleCancel = () => {
    if (onClose) onClose();
    navigate("/");
  };

  const handleTopup = () => {
    if (onClose) onClose();
    navigate("/topup", { state: { cardId, cardAddress } });
  };

  const truncateAddress = (addr) =>
    addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : "";

  /**
   * Generate compact text format for NFC
   * Format: "cardId|share2"
   */
  const generateCompactText = () => {
    try {
      // Clean share2 (remove 0x prefix if present)
      const share2Clean = share2.startsWith("0x") ? share2.slice(2) : share2;
      const compactText = `${cardId}|${share2Clean}`;

      return {
        text: compactText,
        size: compactText.length,
        fitsInTag: compactText.length <= 144,
      };
    } catch (err) {
      console.error("Failed to generate compact text:", err);
      return null;
    }
  };

  /**
   * Copy card data for manual NFC writing
   */
  const handleCopyCardData = () => {
    const data = generateCompactText();

    if (!data) {
      alert("❌ Failed to generate card data");
      return;
    }

    if (!data.fitsInTag) {
      alert(`❌ Data too large: ${data.size} characters (limit: 144)`);
      return;
    }
    navigator.clipboard
      .writeText(data.text)
      .then(() => {
        alert(
          `✅ Card data copied!\n\n` +
          `Instructions:\n` +
          `1. Open NFC TagWriter app\n` +
          `2. New Record → Text\n` +
          `3. Paste the copied data\n` +
          `4. Write to your NFC tag`,
        );
      })
      .catch((err) => {
        alert(`Card Data:\n\n${data.text}`);
      });
  };

  /**
   * Write to NFC tag directly (Android Chrome)
   */
  const writeToNfc = async () => {
    if (!nfcSupported) {
      alert(
        "NFC writing is not supported on this browser.\n\nPlease use the 'Write Manually' option.",
      );
      return;
    }

    try {
      setWritingNfc(true);

      const data = generateCompactText();
      if (!data || !data.fitsInTag) {
        throw new Error("Data too large for NFC tag");
      }

      const ndef = new window.NDEFReader();
      await ndef.write({
        records: [
          {
            recordType: "text",
            data: data.text,
          },
        ],
      });

      setWritingNfc(false);
      alert(`✅ NFC write successful!\n\nYour card is now ready to use.`);
    } catch (err) {
      setWritingNfc(false);

      if (err.name === "NotAllowedError") {
        alert(
          "❌ NFC permission denied.\n\nPlease grant NFC permission and try again.",
        );
      } else if (err.name === "NotSupportedError") {
        alert(
          "❌ NFC not supported on this browser.\n\nPlease use 'Write Manually' option.",
        );
        setNfcSupported(false);
      } else if (err.name === "NotReadableError") {
        alert(
          "❌ NFC tag not detected.\n\nPlease hold your NFC tag close to the phone and try again.",
        );
      } else {
        alert(
          `❌ Write failed: ${err.message}\n\nPlease use 'Write Manually' option.`,
        );
      }
    }
  };

  const toggleManualWrite = () => {
    setShowManualWrite(!showManualWrite);
  };
  const dataSize = generateCompactText()?.size || 0;
  const percentUsed = Math.round((dataSize / 144) * 100);
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-2xl w-full max-w-md shadow-xl text-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <h2 className="text-xl font-semibold mb-4 text-center">
          🎉 Card Created Successfully!
        </h2>
        {/* Card Info */}
        <div className="bg-gray-800 p-4 rounded-xl mb-4">
          <div className="flex justify-between mb-2">
            <span className="text-gray-400 text-sm">Card ID</span>
            <span className="font-medium text-xs">{cardId}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400 text-sm">Address</span>
            <span className="font-medium text-xs">
              {truncateAddress(cardAddress)}
            </span>
          </div>
        </div>

        {/* Data Size Info */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-blue-200 text-xs">NFC Data Size</span>
            <span className="text-blue-300 font-semibold text-sm">
              {dataSize} / 144 bytes
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all"
              style={{ width: `${percentUsed}%` }}
            ></div>
          </div>
          <p className="text-blue-200 text-xs mt-1">
            ✓ Fits in tag ({percentUsed}% used)
          </p>
        </div>

        {/* Main Content */}
        {!showManualWrite ? (
          <>
            {/* Auto Write Button */}
            {nfcSupported && (
              <button
                onClick={writeToNfc}
                disabled={writingNfc}
                className={`w-full py-3 rounded-xl font-semibold mb-3 transition ${writingNfc
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700"
                  }`}
              >
                {writingNfc ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Hold NFC tag near phone...
                  </span>
                ) : (
                  "📱 Write to NFC Tag Now"
                )}
              </button>
            )}

            {/* Instructions */}
            {nfcSupported && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-3">
                <p className="text-blue-200 text-xs font-semibold mb-1">
                  How to write:
                </p>
                <ol className="text-blue-200 text-xs ml-4 list-decimal space-y-0.5">
                  <li>Tap the button above</li>
                  <li>Hold NFC tag to back of phone</li>
                  <li>Keep steady until success message</li>
                </ol>
              </div>
            )}

            {/* Manual Option */}
            <button
              onClick={toggleManualWrite}
              className="w-full bg-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-600 transition mb-4"
            >
              {isIOS ? "📱 Write Using App (iPhone)" : "✍️ Write Manually"}
            </button>
          </>
        ) : (
          <>
            {/* Manual Write Section */}
            <div className="bg-gray-800 p-4 rounded-xl mb-4">
              <h3 className="text-sm font-semibold text-blue-300 mb-3">
                Write Using NFC App
              </h3>

              {/* Platform Badge */}
              <div
                className={`mb-3 px-3 py-2 rounded-lg text-xs text-center ${isIOS
                    ? "bg-purple-500/20 border border-purple-500/30 text-purple-200"
                    : "bg-blue-500/20 border border-blue-500/30 text-blue-200"
                  }`}
              >
                {isIOS ? "📱 iPhone" : "🤖 Android"}
              </div>
              {/* Copy Button */}
              <button
                onClick={handleCopyCardData}
                className="w-full bg-green-600 hover:bg-green-700 py-3 rounded-lg font-semibold text-sm transition mb-3"
              >
                📋 Copy Card Data
              </button>

              {/* Instructions */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-200 text-xs font-semibold mb-2">
                  Instructions:
                </p>
                <ol className="text-blue-200 text-xs ml-4 list-decimal space-y-1">
                  <li>
                    Install <strong>NFC TagWriter</strong> app
                  </li>
                  <li>Tap "Copy Card Data" button above</li>
                  <li>
                    Open app → New Record → <strong>Text</strong>
                  </li>
                  <li>Paste the copied data</li>
                  <li>Write to your NFC tag</li>
                </ol>
              </div>
            </div>

            {/* Back Button */}
            {nfcSupported && (
              <button
                onClick={toggleManualWrite}
                className="w-full bg-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-600 transition mb-4"
              >
                ← Back
              </button>
            )}
          </>
        )}
        {/* Action Buttons */}
        <div className="flex gap-3 mb-4">
          <button
            onClick={handleCancel}
            className="flex-1 bg-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-600 transition"
          >
            Done
          </button>
          <button
            onClick={handleTopup}
            className="flex-1 bg-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-700 transition"
          >
            Top Up Card
          </button>
        </div>
        {/* Security Warning */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <p className="text-red-200 text-xs text-center">
            ⚠️ <strong>Keep your NFC card secure.</strong> Anyone with the card
            can make payments.
          </p>
        </div>
      </div>
    </div>
  );
}
